import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { StatusPill, V3_STATUSES } from "@/components/admin/StatusPill";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { PlusCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminIssuesListPage() {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (status) params.set("status", status);
      const r = await api.get(`/admin/issues?${params.toString()}`);
      setIssues(r.data || []);
      // Prune selection to only ids that still exist in the current list.
      setSelected((prev) => {
        const next = new Set();
        const ids = new Set((r.data || []).map((i) => i.id));
        prev.forEach((id) => { if (ids.has(id)) next.add(id); });
        return next;
      });
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [status]);

  const create = async () => {
    try {
      const r = await api.post("/admin/issues", {});
      window.location.href = `/admin/issues/${r.data.id}`;
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create"); }
  };

  const archivedIds = useMemo(
    () => new Set(issues.filter((i) => i.status === "archived").map((i) => i.id)),
    [issues]
  );
  const selectedArchivedCount = useMemo(
    () => Array.from(selected).filter((id) => archivedIds.has(id)).length,
    [selected, archivedIds]
  );
  const anyArchived = archivedIds.size > 0;

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAllArchived = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      const allArchivedIds = Array.from(archivedIds);
      const allSelected = allArchivedIds.every((id) => next.has(id));
      if (allSelected) {
        allArchivedIds.forEach((id) => next.delete(id));
      } else {
        allArchivedIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const runDelete = async () => {
    const ids = Array.from(selected).filter((id) => archivedIds.has(id));
    if (ids.length === 0) return;
    setDeleting(true);
    let ok = 0, fail = 0;
    for (const id of ids) {
      try {
        await api.delete(`/admin/issues/${id}`);
        ok += 1;
      } catch (e) {
        fail += 1;
      }
    }
    setDeleting(false);
    setConfirmOpen(false);
    setSelected(new Set());
    if (fail === 0) {
      toast.success(`Deleted ${ok} issue${ok === 1 ? "" : "s"}`);
    } else {
      toast.error(`Deleted ${ok}, ${fail} failed`);
    }
    load();
  };

  const allArchivedSelected = archivedIds.size > 0 && Array.from(archivedIds).every((id) => selected.has(id));

  return (
    <AdminLayout>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="kicker">Issues</div>
          <h1 className="mt-1 font-display text-3xl font-bold">All issues</h1>
        </div>
        <div className="flex items-center gap-2">
          {selectedArchivedCount > 0 && (
            <Button
              variant="destructive"
              onClick={() => setConfirmOpen(true)}
              data-testid="admin-issues-delete-selected"
              className="rounded-full"
            >
              <Trash2 className="h-4 w-4 mr-2" /> Delete {selectedArchivedCount} archived
            </Button>
          )}
          <Button onClick={create} data-testid="admin-issues-create-button" className="bg-[color:var(--coh-blue)] text-white hover:bg-[color:var(--coh-blue)]/90 rounded-full">
            <PlusCircle className="h-4 w-4 mr-2" /> New issue
          </Button>
        </div>
      </div>

      <Card className="mt-6 p-4">
        <div className="flex flex-wrap gap-3">
          <div className="flex-1 min-w-[200px]">
            <Input placeholder="Search title…" value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => e.key === "Enter" && load()} data-testid="admin-issues-search-input" />
          </div>
          <Select value={status || "__ALL__"} onValueChange={(v) => setStatus(v === "__ALL__" ? "" : v)}>
            <SelectTrigger data-testid="admin-issues-status-filter" className="w-[220px]"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__ALL__">All statuses</SelectItem>
              {V3_STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g,' ')}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load}>Search</Button>
        </div>
      </Card>

      <Card className="mt-4 p-0 overflow-hidden">
        <table data-testid="admin-issues-table" className="w-full text-sm admin-table">
          <thead className="bg-secondary border-b border-border">
            <tr>
              <th className="px-3 py-3 text-left w-10">
                <Checkbox
                  aria-label="Select all archived issues"
                  data-testid="admin-issues-select-all-archived"
                  checked={allArchivedSelected}
                  disabled={!anyArchived}
                  onCheckedChange={toggleAllArchived}
                />
              </th>
              <th className="px-4 py-3 text-left">#</th>
              <th className="px-4 py-3 text-left">Title</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Theme</th>
              <th className="px-4 py-3 text-left">Updated</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">Loading…</td></tr>}
            {!loading && issues.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No issues yet.</td></tr>
            )}
            {!loading && issues.map((i) => {
              const isArchived = i.status === "archived";
              return (
                <tr key={i.id} className="border-b border-border last:border-b-0 hover:bg-secondary/60">
                  <td className="px-3 py-3">
                    {isArchived ? (
                      <Checkbox
                        aria-label={`Select issue ${i.issue_number}`}
                        data-testid={`admin-issues-row-checkbox-${i.id}`}
                        checked={selected.has(i.id)}
                        onCheckedChange={() => toggleOne(i.id)}
                      />
                    ) : (
                      <span className="inline-block h-4 w-4" aria-hidden="true" />
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">#{String(i.issue_number).padStart(3, "0")}</td>
                  <td className="px-4 py-3">
                    <Link to={`/admin/issues/${i.id}`} className="font-medium hover:underline underline-offset-4">{i.title || "(Untitled)"}</Link>
                  </td>
                  <td className="px-4 py-3"><StatusPill status={i.status} /></td>
                  <td className="px-4 py-3 text-muted-foreground">{i.theme || "—"}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{i.updated_at ? new Date(i.updated_at).toLocaleString() : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      <p className="mt-3 text-xs text-muted-foreground">
        Deletion is permanent and only allowed for <span className="font-semibold text-[color:var(--coh-ink)]">archived</span> issues.
        To remove a published issue, archive it first, then delete.
      </p>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="admin-issues-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedArchivedCount} archived issue{selectedArchivedCount === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the selected issue{selectedArchivedCount === 1 ? "" : "s"} and all of their revision history. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={runDelete}
              disabled={deleting}
              data-testid="admin-issues-delete-confirm-button"
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              {deleting ? "Deleting…" : "Delete permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
